import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import BusinessIcon from '@mui/icons-material/Business';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import { GEMINI_GRADIENT } from '../theme';
import { AuthServerError, authServerAuthApi, loginBlockedReason, type AuthServerMfaSetupResponse } from '../api/authServerAuth';
import { resolvePostLoginPath } from '../config/paths';
import { formatAdminCredentialErrors, validateAdminEmailIssues } from '../lib/adminPasswordPolicy';
import { normalizeAdminMfaRecoveryCode } from '../lib/mfaChallengeSession';
import { readRememberDevicePreference, writeRememberDevicePreference } from '../lib/mfaRememberDevice';
import { adminOidcConfig } from '../lib/oidc/oidcConfig';
import {
  OIDC_REDIRECT_LOOP_WINDOW_MS,
  initialLoginStep,
  parseAuthServerLoginParams,
  resolveAuthServerRedirect,
  type AuthServerLoginParams,
  type LoginPageStep,
} from '../lib/oidc/oidcCore';
import { lastOidcReauthorizeAt, peekOidcTransaction, startOidcAuthorize } from '../lib/oidc/oidcSession';
import { downloadRecoveryCodes } from './MfaPage';
import {
  passkeysSupported,
  serializeAssertion,
  serializeAttestation,
  toCreationOptions,
  toRequestOptions,
  type CreationOptionsJSON,
  type RequestOptionsJSON,
} from '../lib/oidc/passkeyCodec';
import {
  forgetEnrolmentCode,
  normalizeEnrolmentCode,
  passkeyErrorAction,
  passkeyErrorText,
  rememberEnrolmentCode,
} from '../lib/oidc/passkeyFlow';

/** Browser WebAuthn ceremonies → the JSON auth-server verifies. */
async function getAssertion(options: RequestOptionsJSON) {
  const cred = await navigator.credentials.get({ publicKey: toRequestOptions(options) });
  if (!cred) throw new Error('No passkey was selected.');
  return serializeAssertion(cred as never);
}

async function createAttestation(options: CreationOptionsJSON) {
  const cred = await navigator.credentials.create({ publicKey: toCreationOptions(options) });
  if (!cred) throw new Error('No passkey was created.');
  return serializeAttestation(cred as never);
}

type Step = LoginPageStep | 'recovery' | 'codes' | 'redirecting';

const primaryButtonSx = {
  py: 1.4,
  fontWeight: 700,
  background: GEMINI_GRADIENT,
  '&:hover': { background: GEMINI_GRADIENT, opacity: 0.9 },
} as const;

function errorText(err: unknown, fallback: string): string {
  if (err instanceof AuthServerError) {
    switch (loginBlockedReason(err)) {
      case 'email-verification':
        return 'This email address is not verified yet. Verify it from the sign-up email, then sign in again.';
      case 'must-reset':
        return 'You must reset your password before signing in. Ask a super administrator for a reset.';
      default:
        return err.message || fallback;
    }
  }
  return fallback;
}

const digits6 = (v: string) => v.replace(/\D/g, '').slice(0, 6);

/**
 * RBAC Phase 3 — `/login` in `VITE_ADMIN_AUTH=oidc` mode.
 *
 * - `?redirect=<authorize url>[&mfa=1|&mfa_enroll=1]` (sent by auth-server): this page IS the
 *   `admin-console` login_url. Credentials / TOTP / recovery / enrolment go to auth-server through
 *   `VITE_AUTH_SERVER_BASE` (cookie session), then the browser follows auth-server's `redirectTo`.
 * - `&phr=1` (RBAC Phase 4): the request asked for `acr_values=phr` — confirm with a passkey
 *   (registering one first if needed). A passkey can also be used to sign in from the start.
 * - Without `redirect`: the SPA's own entry — start an interactive authorize (auto when a guard
 *   sent us here with `?return=`, unless we just did; a button after sign-out).
 */
export function OidcLoginPage() {
  const [search] = useSearchParams();
  const cfg = useMemo(() => adminOidcConfig(), []);
  const params = useMemo<AuthServerLoginParams | null>(
    () =>
      parseAuthServerLoginParams(search.toString(), {
        authServerBase: cfg.authServerBase,
        issuer: cfg.issuer,
        appOrigin: window.location.origin,
      }),
    [cfg, search],
  );
  // `?enrolment=<code>` (a link from an IAM admin) is kept for this tab across the authorize
  // round trip, so the passkey step can send it without the user typing it.
  const enrolmentCode = useMemo(() => rememberEnrolmentCode(search.toString()), [search]);

  if (!params) return <OidcSignInEntry />;
  return <AuthServerLoginForm params={params} initialEnrolmentCode={enrolmentCode} />;
}

function OidcSignInEntry() {
  const [search] = useSearchParams();
  const returnParam = search.get('return');
  const signedOut = search.get('state') === 'signed_out';
  const invalidRedirect = search.has('redirect');
  const started = useRef(false);

  const last = lastOidcReauthorizeAt();
  const recentlyTried = last !== null && Date.now() - last < OIDC_REDIRECT_LOOP_WINDOW_MS;
  const autoStart = !!returnParam && !signedOut && !invalidRedirect && !recentlyTried;

  const signIn = () => void startOidcAuthorize({ kind: 'interactive', returnTo: resolvePostLoginPath(returnParam) });

  useEffect(() => {
    if (!autoStart || started.current) return;
    started.current = true;
    signIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  return (
    <LoginShell title={autoStart ? 'One moment…' : 'Admin sign in'} subtitle="Sign in with your Cash4You staff account.">
      {autoStart ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress size={36} thickness={3} />
        </Box>
      ) : (
        <Stack spacing={2}>
          {signedOut && <Alert severity="success" sx={{ borderRadius: 2 }}>You have signed out.</Alert>}
          {invalidRedirect && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              The sign-in link was not valid for this console. Start sign-in again.
            </Alert>
          )}
          {recentlyTried && returnParam && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Your sign-in could not be completed for the admin console. If this keeps happening, your
              account may not have admin access — contact a super administrator.
            </Alert>
          )}
          <Button fullWidth variant="contained" size="large" onClick={signIn} sx={primaryButtonSx}>
            Sign in
          </Button>
        </Stack>
      )}
    </LoginShell>
  );
}

function AuthServerLoginForm({ params, initialEnrolmentCode }: { params: AuthServerLoginParams; initialEnrolmentCode: string }) {
  const cfg = useMemo(() => adminOidcConfig(), []);
  const [step, setStep] = useState<Step>(() => initialLoginStep(params));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totp, setTotp] = useState('');
  const [recovery, setRecovery] = useState('');
  const [rememberDevice, setRememberDevice] = useState(() => readRememberDevicePreference());
  const [setup, setSetup] = useState<AuthServerMfaSetupResponse | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [pendingRedirect, setPendingRedirect] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [needsPasskeyEnrol, setNeedsPasskeyEnrol] = useState(false);
  // Passkey hardening: the one-time code for a first passkey, a pending "confirm with an existing
  // passkey first", and the cooldown notice for a passkey that is too new for critical actions.
  const [enrolmentCode, setEnrolmentCode] = useState(initialEnrolmentCode);
  const [enrolmentCodeAsked, setEnrolmentCodeAsked] = useState(false);
  const [needsPasskeyReauth, setNeedsPasskeyReauth] = useState(false);
  const [notice, setNotice] = useState('');
  const canUsePasskeys = useMemo(() => passkeysSupported(), []);

  /** Follow auth-server's `redirectTo` through the auth-server base; fall back to `redirect`. */
  const follow = (redirectTo: string | null | undefined) => {
    setStep('redirecting');
    window.location.assign(resolveAuthServerRedirect(redirectTo, cfg.authServerBase) ?? params.redirect);
  };

  const run = async (fn: () => Promise<void>, fallback: string) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(errorText(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  // mfa_enroll=1: fetch the TOTP secret / QR once.
  const setupRequested = useRef(false);
  useEffect(() => {
    if (step !== 'mfa-enroll' || setup || setupRequested.current) return;
    setupRequested.current = true;
    void run(async () => setSetup(await authServerAuthApi.setupMfa()), 'Could not start authenticator setup.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, setup]);

  const submitCredentials = (event: React.FormEvent) => {
    event.preventDefault();
    const issues = validateAdminEmailIssues(email);
    if (!email || !password) {
      setError('Enter your email and password.');
      return;
    }
    if (issues.length) {
      setError(formatAdminCredentialErrors(issues));
      return;
    }
    void run(async () => {
      const res = await authServerAuthApi.login(email.trim(), password);
      setPassword(''); // never kept beyond the request
      if (res.mfaRequired) {
        setStep('mfa');
        return;
      }
      follow(res.redirectTo); // MFA not enrolled → auth-server sends us back with mfa_enroll=1
    }, 'Sign-in failed. Try again.');
  };

  const submitTotp = () =>
    run(async () => {
      writeRememberDevicePreference(rememberDevice);
      const res = await authServerAuthApi.verifyTotp(totp, rememberDevice);
      follow(res.redirectTo);
    }, 'Could not verify the authenticator code.').finally(() => setTotp(''));

  const submitRecovery = () =>
    run(async () => {
      const res = await authServerAuthApi.verifyRecoveryCode(normalizeAdminMfaRecoveryCode(recovery));
      follow(res.redirectTo);
    }, 'Could not verify the recovery code.');

  const submitEnable = () =>
    run(async () => {
      const res = await authServerAuthApi.enableMfa(totp);
      setRecoveryCodes(res.recoveryCodes ?? []);
      setPendingRedirect(res.redirectTo ?? null);
      setStep('codes');
    }, 'Could not enable the authenticator.').finally(() => setTotp(''));

  /** A passkey ceremony: errors use passkey wording; "no passkey yet" switches to enrolment. */
  const runPasskey = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
    } catch (err) {
      const action = passkeyErrorAction(err);
      if (action === 'enrol') setNeedsPasskeyEnrol(true);
      if (action === 'enrolment-code') {
        setNeedsPasskeyEnrol(true);
        setEnrolmentCodeAsked(true);
        // A refused code (expired, used, replaced) is not offered again.
        if (enrolmentCode) {
          forgetEnrolmentCode();
          setEnrolmentCode('');
        }
      }
      if (action === 'passkey-reauth') setNeedsPasskeyReauth(true);
      if (action === 'too-new') {
        // The passkey is saved and possession recorded; only the critical-action check must wait.
        setNeedsPasskeyEnrol(false);
        setNotice(passkeyErrorText(err));
        return;
      }
      setError(action === 'enrol' ? '' : passkeyErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const signInWithPasskey = () => {
    const issues = validateAdminEmailIssues(email);
    if (!email || issues.length) {
      setError(email ? formatAdminCredentialErrors(issues) : 'Enter your email, then choose Sign in with a passkey.');
      return;
    }
    void runPasskey(async () => {
      const { options } = await authServerAuthApi.passkeyLoginOptions(email.trim());
      const res = await authServerAuthApi.passkeyLoginVerify(email.trim(), await getAssertion(options));
      follow(res.redirectTo);
    });
  };

  const confirmWithPasskey = () =>
    runPasskey(async () => {
      const { options } = await authServerAuthApi.passkeyStepUpOptions();
      const res = await authServerAuthApi.passkeyStepUpVerify(await getAssertion(options));
      follow(res.redirectTo);
    });

  /**
   * Register a passkey on this device, then confirm with it (registering alone is not a step-up).
   * A first passkey sends the enrolment code; it is single use, so it is forgotten once accepted.
   */
  const registerAndConfirm = async () => {
    const code = normalizeEnrolmentCode(enrolmentCode) || undefined;
    const { options } = await authServerAuthApi.passkeyRegisterOptions(code);
    await authServerAuthApi.passkeyRegisterVerify(await createAttestation(options), undefined, code);
    if (code) {
      forgetEnrolmentCode();
      setEnrolmentCode('');
    }
    setNeedsPasskeyEnrol(false);
    setEnrolmentCodeAsked(false);
    setNeedsPasskeyReauth(false);
    const stepUp = await authServerAuthApi.passkeyStepUpOptions();
    const res = await authServerAuthApi.passkeyStepUpVerify(await getAssertion(stepUp.options));
    follow(res.redirectTo);
  };

  const registerThenConfirm = () => runPasskey(registerAndConfirm);

  /**
   * 401 PASSKEY_REAUTH_REQUIRED: this account already has a passkey, so adding one needs a passkey
   * confirmation in this session first. Confirm with an existing passkey (a PASSKEY_TOO_NEW answer
   * still records possession), then retry the registration.
   */
  const confirmExistingThenRegister = () =>
    runPasskey(async () => {
      const { options } = await authServerAuthApi.passkeyStepUpOptions();
      try {
        await authServerAuthApi.passkeyStepUpVerify(await getAssertion(options));
      } catch (err) {
        if (passkeyErrorAction(err) !== 'too-new') throw err;
      }
      setNeedsPasskeyReauth(false);
      await registerAndConfirm();
    });

  const withProvider = (provider: 'google' | 'bizauth') =>
    void startOidcAuthorize({ kind: 'provider', provider, returnTo: peekOidcTransaction()?.returnTo });

  const titles: Record<Step, [string, string]> = {
    credentials: ['Welcome back', 'Admin sign in — use the account a super administrator created for you.'],
    mfa: ['Authenticator code', 'Enter the 6-digit code from your authenticator app.'],
    recovery: ['Recovery code', 'Enter one of your saved recovery codes.'],
    'mfa-enroll': ['Set up your authenticator', 'Staff accounts need an authenticator app before signing in.'],
    codes: ['Save your recovery codes', 'Each code works once if you lose your authenticator.'],
    passkey: ['Confirm with your passkey', 'This part of the console needs a phishing-resistant sign-in.'],
    redirecting: ['One moment…', 'Finishing sign-in'],
  };

  return (
    <LoginShell title={titles[step][0]} subtitle={titles[step][1]}>
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          {notice}
        </Alert>
      )}

      {step === 'redirecting' && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CircularProgress size={36} thickness={3} />
        </Box>
      )}

      {step === 'credentials' && (
        <>
          <Stack spacing={1.5} sx={{ mb: 2.5 }}>
            <Button fullWidth variant="outlined" startIcon={<GoogleIcon />} onClick={() => withProvider('google')} sx={{ fontWeight: 600 }}>
              Continue with Google
            </Button>
            <Button fullWidth variant="outlined" startIcon={<BusinessIcon />} onClick={() => withProvider('bizauth')} sx={{ fontWeight: 600 }}>
              Continue with BizAuth
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              Staff will be asked for their authenticator code after Google or BizAuth.
            </Typography>
          </Stack>
          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
              or with email
            </Typography>
          </Divider>
          <Box component="form" onSubmit={submitCredentials}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Email address"
                type="email"
                value={email}
                disabled={busy}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                required
                autoComplete="username"
              />
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                disabled={busy}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                required
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button type="submit" fullWidth variant="contained" size="large" disabled={busy} sx={primaryButtonSx}>
                {busy ? 'Please wait…' : 'Admin sign in'}
              </Button>
              {canUsePasskeys && (
                <Button fullWidth variant="outlined" startIcon={<KeyRoundedIcon />} disabled={busy} onClick={signInWithPasskey} sx={{ fontWeight: 600 }}>
                  Sign in with a passkey
                </Button>
              )}
            </Stack>
          </Box>
        </>
      )}

      {step === 'passkey' && (
        <Stack spacing={2}>
          {!canUsePasskeys ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              This browser cannot use passkeys. Open the console in a current browser to continue.
            </Alert>
          ) : needsPasskeyReauth ? (
            <>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Your account already has a passkey. Confirm with it first; then the new passkey on this device is added.
              </Alert>
              <Button fullWidth variant="contained" startIcon={<KeyRoundedIcon />} disabled={busy} onClick={() => void confirmExistingThenRegister()} sx={primaryButtonSx}>
                {busy ? 'Waiting for your passkey…' : 'Confirm with an existing passkey'}
              </Button>
            </>
          ) : needsPasskeyEnrol ? (
            <>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                You have no passkey yet. Register one on this device (Touch ID, Windows Hello, your phone or a security
                key), then confirm with it. Your first passkey needs the one-time enrolment code an IAM admin gave you.
              </Alert>
              <TextField
                fullWidth
                label="Enrolment code"
                placeholder="pket_…"
                value={enrolmentCode}
                disabled={busy}
                error={enrolmentCodeAsked && !normalizeEnrolmentCode(enrolmentCode)}
                helperText={
                  enrolmentCodeAsked && !normalizeEnrolmentCode(enrolmentCode)
                    ? 'Ask an IAM admin for a passkey enrolment code.'
                    : 'From the enrolment link or message an IAM admin sent you.'
                }
                onChange={(e) => {
                  setEnrolmentCode(e.target.value);
                  setError('');
                }}
                inputProps={{ autoComplete: 'off', spellCheck: false }}
              />
              <Button fullWidth variant="contained" startIcon={<KeyRoundedIcon />} disabled={busy} onClick={() => void registerThenConfirm()} sx={primaryButtonSx}>
                {busy ? 'Waiting for your passkey…' : 'Register a passkey'}
              </Button>
            </>
          ) : (
            <Button fullWidth variant="contained" startIcon={<KeyRoundedIcon />} disabled={busy} onClick={() => void confirmWithPasskey()} sx={primaryButtonSx}>
              {busy ? 'Waiting for your passkey…' : 'Confirm with passkey'}
            </Button>
          )}
        </Stack>
      )}

      {step === 'mfa' && (
        <Stack spacing={2}>
          <TextField
            label="Authenticator code"
            value={totp}
            onChange={(e) => setTotp(digits6(e.target.value))}
            inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }}
            fullWidth
            autoFocus
          />
          <FormControlLabel
            control={<Checkbox checked={rememberDevice} onChange={(_, c) => setRememberDevice(c)} disabled={busy} />}
            label="Remember this device"
          />
          <Button fullWidth variant="contained" disabled={busy || totp.length !== 6} onClick={() => void submitTotp()} sx={primaryButtonSx}>
            {busy ? 'Verifying…' : 'Verify and continue'}
          </Button>
          <Button variant="text" onClick={() => { setError(''); setStep('recovery'); }}>
            Use a recovery code instead
          </Button>
        </Stack>
      )}

      {step === 'recovery' && (
        <Stack spacing={2}>
          <TextField label="Recovery code" value={recovery} onChange={(e) => setRecovery(e.target.value)} fullWidth autoFocus />
          <Button fullWidth variant="contained" disabled={busy || !recovery.trim()} onClick={() => void submitRecovery()} sx={primaryButtonSx}>
            {busy ? 'Verifying…' : 'Verify recovery code'}
          </Button>
          <Button variant="text" onClick={() => { setError(''); setStep('mfa'); }}>
            Back to authenticator code
          </Button>
        </Stack>
      )}

      {step === 'mfa-enroll' && (
        <Stack spacing={2}>
          {!setup ? (
            busy ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress size={36} thickness={3} />
              </Box>
            ) : (
              <Button
                variant="outlined"
                onClick={() => void run(async () => setSetup(await authServerAuthApi.setupMfa()), 'Could not start authenticator setup.')}
              >
                Try again
              </Button>
            )
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Scan this QR code with Google Authenticator, Microsoft Authenticator or another TOTP app, then enter
                the 6-digit code.
              </Typography>
              <Box
                component="img"
                src={setup.qrCode}
                alt="Authenticator QR code"
                sx={{ width: 200, height: 200, mx: 'auto', display: 'block', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all', textAlign: 'center' }}>
                Can't scan? Enter this key: <strong>{setup.secret}</strong>
              </Typography>
              <TextField
                label="Authenticator code"
                value={totp}
                onChange={(e) => setTotp(digits6(e.target.value))}
                inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }}
                fullWidth
              />
              <Button fullWidth variant="contained" disabled={busy || totp.length !== 6} onClick={() => void submitEnable()} sx={primaryButtonSx}>
                {busy ? 'Enabling…' : 'Enable authenticator'}
              </Button>
            </>
          )}
        </Stack>
      )}

      {step === 'codes' && (
        <Stack spacing={2}>
          <Box component="pre" sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', fontFamily: 'monospace', fontSize: '0.85rem', overflow: 'auto', m: 0 }}>
            {recoveryCodes.join('\n')}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<DownloadIcon />} disabled={!recoveryCodes.length} onClick={() => downloadRecoveryCodes(recoveryCodes)}>
              Download
            </Button>
            <Button fullWidth variant="contained" onClick={() => follow(pendingRedirect)} sx={primaryButtonSx}>
              Continue to console
            </Button>
          </Stack>
        </Stack>
      )}
    </LoginShell>
  );
}

function LoginShell({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', p: 2 }}>
      <Paper elevation={0} sx={{ maxWidth: 440, width: 1, borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Box sx={{ height: 4, background: GEMINI_GRADIENT }} />
        <Box sx={{ p: 4, pt: 3 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Admin console
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
            {subtitle}
          </Typography>
          {children}
        </Box>
      </Paper>
    </Box>
  );
}
